import { Accordion, AccordionDetails, AccordionSummary, FormControl } from '@material-ui/core';
import React, { useEffect, useRef } from 'react';
import RadioRhf from '../components/RadioRhf/RadioRhf';
import { RadioOptionRhf } from '../components/RadioRhf/RadioOptionRhf';
import TeethEquipmentRhf from './Question/TeethEquipmentRhf';
import useResetRhfFields from './hooks/useResetField';
import ClearRetainerRhf from './Question/ClearRetainerRhf';
import { ExpandMore } from '@material-ui/icons';
import useSyncFieldToForm from '../../../pages/hooks/DiffRetainerHooks/useSyncFieldToForm';
import PrescriptionBtnGroup from './PrescriptionBtnGroup';
import { RequestRetainerProps } from '../../../pages/DiffRetainer/type';
import FileUploadQuestion from './FileUploadQuestion';
import { useFormContext } from 'react-hook-form';
import useTeethContext from './hooks/useTeethContext';
import useDiffRetainerLangContext from './hooks/useDiffRetainerLangContext';
import SpecialInstruction from './SpecialInstruction';
import usePonticOption from './hooks/usePonticOption';
import RetainerTypeFormControlLabelRhf from './RetainerTypeFormControlLabelRhf';
import useFormQuestionEnable from './hooks/useFormQuestionEnable';
import useResetSubOption from './hooks/useResetSubOption';
import { SelectRhf } from '../components/selectRhf/SelectRhf';
import useSetTriggerValue from './hooks/useSetTriggerValue';
import { useLang } from '../../../util/lang';
import { useDispatch, useSelector } from 'react-redux';
import { RESET_TOOTH_SELECTIONS, SET_SPECIAL_INSTRUCTION } from '../../../store/DiffRetainers/actionType';
import { IMPRESSION_TYPE } from '../../../pages/hooks/DiffRetainerHooks/useRetainerRequestForm';
import { isRetainerEcodontOrClearRetainer } from '../../../pages/DiffRetainer/utils';
import useRetainerCommon from './hooks/useRetainerCommon';

/**
 * `BasePrescriptionNote` is a component that renders a prescription note for single the different retainer page.
 *
 * @component
 * @param {RequestRetainerProps} props - The props that define the component.
 */
export const BasePrescriptionNote = (props) => {
    const { uiProps, oauthProps, formProps, onError, onSubmit, questions, addOnProps, isTab, isReSubmit } = props;
    const { impressionTypeSummary, impressionType, setImpressionType, expanded, setExpanded, handleExpand, isNewCase } =
        uiProps;
    const { threeshapeConnected, setThreeshapeConnected } = oauthProps;
    const { errors, formValues } = formProps;
    const { handleSubmit, control, reset, resetField, setValue, trigger, register } = useFormContext();
    const { baseTeethSelections } = useTeethContext();
    const { toothForBiteRamp, toothForPontics, setToothForBiteRamp, setToothForPontics } = baseTeethSelections;
    const { setSpecialInstruction, specialInstruction, resetAddOnFields } = addOnProps;
    const diffRetainer = useSelector((state) => state.diffRetainer);
    const dispatch = useDispatch();

    const { getDiffRetainerLang } = useDiffRetainerLangContext();
    const getGlobalLang = useLang('global');

    const { memoPonticOption } = usePonticOption({ selectedRetainerType: formValues[questions[3].name] });

    const { retainerDesignQuestion } = useRetainerCommon({
        selectedRetainer: formValues['archAndQuantity'],
        impressionType,
        subType: undefined,
        getDiffRetainerLang,
        option: {
            fixedWire: formValues['fixedWire'],
            clearRetainerDesignUpperOption: formValues['clearRetainerDesignUpperOption'],
            clearRetainerDesignLowerOption: formValues['clearRetainerDesignLowerOption'],
            archAndQuantity: formValues['archAndQuantity'],
        },
    });

    const {
        enableOccBiteRampQuestion, // Q4
        enableClearRetainerDesignQuestion, // Q5
        // enableClearRetainerDesignQuestionForSecondSetRetainer, // Q6
        enablePontic,
        enableUploadImpression,
    } = useFormQuestionEnable({ formValues, impressionType });

    // useEffect(() => {
    //     if(impressionType) {
    //         setExpanded('panel1')
    //     } else setExpanded(false)
    // },[impressionType])

    // updating form's field based on external state value
    // need use with a hidden input (e.g. => search for example => ...register("archPosition"))
    useSyncFieldToForm(impressionType, setValue, trigger, '', questions[1].name);

    useResetSubOption({
        resetField,
        subOption: [
            'bleachingRetainersSelect',
            'palatalWireSelect',
            'smartFitWireSelect',
            'ecodontWireSelect',
            'ecodontRetainerWithWire_1setSelect',
            'LowTrimlineAreaSelect_3',
            'LowTrimlineAreaSelect_1',
            'jigPlacementSelect',

            'pontic',
            'ponticColor',
        ],
        trackValue: formValues[questions[3].name],
    });

    useResetRhfFields(
        formValues[questions[2].name],
        formValues[questions[2].name] === 'true' || formValues[questions[2].name] === 'false',
        [
            'archAndQuantity',
            'LowTrimlineAreaSelect_3',
            'LowTrimlineAreaSelect_1',
            'bleachingRetainersSelect',
            'palatalWireSelect',
            'smartFitWireSelect',
            'ecodontWireSelect',
            'ecodontRetainerWithWire_1setSelect',

            'jigPlacementSelect',

            'clearRetainerDesignUpperOption',
            'clearRetainerDesignLowerOption',

            'pontic',
            'pontics',
            'ponticColor',
            'occlusalBiteRampQuestion',
        ],
        resetField
    );

    useResetRhfFields(
        formValues[questions[3].name],
        !enableOccBiteRampQuestion,
        ['occlusalBiteRampQuestion'],
        resetField
    );

    useResetRhfFields(
        formValues[questions[3].name],
        !enableClearRetainerDesignQuestion,
        [
            //  'clearRetainerDesignUpper',
            'clearRetainerDesignUpperOption',
            //  'clearRetainerDesignLower',
            'clearRetainerDesignLowerOption',
        ],
        resetField
    );
    useResetRhfFields(
        formValues[questions[3].name],
        [
            //  'clearRetainerDesignUpper',
            'clearRetainerDesignUpperOption',
            //  'clearRetainerDesignLower',
            'clearRetainerDesignLowerOption',
        ],
        resetField
    );
    useResetRhfFields(
        formValues[questions['pontic'].name],
        formValues[questions['pontic'].name] !== 'colourPonticRequired',
        ['ponticColor'],
        resetField
    );

    // this ref and effect for setting default value and reset value when retainer type is changed for new case
    // const prevFormValuesRef = useRef();

    // useEffect(() => {
    //     // Compare current and previous values
    //     if (prevFormValuesRef.current) {
    //         const prevFormValues = prevFormValuesRef.current;
    //         if (formValues['archAndQuantity'] !== prevFormValues['archAndQuantity']) {
    //             if(impressionType === IMPRESSION_TYPE.UPPER) {
    //                 if(formValues['fixedWire'] === 'true' && prevFormValues['clearRetainerDesignUpperOption'] !== 'NotCoverLingualOrPalatalWire') {
    //                     setValue('clearRetainerDesignUpperOption' , 'NotCoverLingualOrPalatalWire')

    //                     if(isRetainerEcodontOrClearRetainer(formValues['archAndQuantity'])) {
    //                         setValue('clearRetainerDesignUpperOptionForSecondSet' , 'NotCoverLingualOrPalatalWire')
    //                     } else setValue('clearRetainerDesignUpperOptionForSecondSet' , undefined)
    //                  }
    //             }

    //             if(impressionType === IMPRESSION_TYPE.LOWER) {
    //                 if(formValues['fixedWire'] === 'true' && prevFormValues['clearRetainerDesignLowerOption'] !== 'NotCoverLingualOrPalatalWire') {
    //                     setValue('clearRetainerDesignLowerOption' , 'NotCoverLingualOrPalatalWire')
    //                     if(isRetainerEcodontOrClearRetainer(formValues['archAndQuantity'])) {
    //                         setValue('clearRetainerDesignLowerOptionForSecondSet' , 'NotCoverLingualOrPalatalWire')
    //                     } else setValue('clearRetainerDesignLowerOptionForSecondSet' , undefined)
    //                  }
    //             }
    //         }
    //     }

    //     // Update the ref with the current form values
    //     prevFormValuesRef.current = formValues;
    // }, [formValues, setValue, impressionType]);

    // useSetTriggerValue({
    //     formMethods : {setValue , trigger},
    //     condition :isRetainerEcodontOrClearRetainer(formValues['archAndQuantity']) && formValues['fixedWire'] === 'true' && formValues['clearRetainerDesignUpperOption'],
    //     fields : ['clearRetainerDesignUpperOptionForSecondSet'],
    //     value : formValues['clearRetainerDesignUpperOption'] ,
    //     formValues,
    //     isReset : true
    // })

    // useSetTriggerValue({
    //     formMethods : {setValue , trigger},
    //     condition :isRetainerEcodontOrClearRetainer(formValues['archAndQuantity']) && formValues['fixedWire'] === 'true' && formValues['clearRetainerDesignLowerOption'],
    //     fields : ['clearRetainerDesignLowerOptionForSecondSet'],
    //     value : formValues['clearRetainerDesignLowerOption'],
    //     formValues,
    //     isReset : true
    // })

    useSetTriggerValue({
        formMethods: { setValue, trigger },
        condition: formValues['pontic'] === 'noPonticColour',
        fields: ['ponticColor'],
        value: null,
        formValues,
        isReset: true,
    });

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Accordion
                    expanded={true}
                    onChange={handleExpand('panel1')}
                    disabled={!impressionType}
                    style={{ display: !impressionType && 'none' }}
                >
                    <AccordionSummary
                        // expandIcon={<ExpandMore/>}
                        aria-controls="panel1a-content"
                        id="panel1a-headrer"
                        style={{ height: 25 }}
                    >
                        <h4>{impressionTypeSummary}</h4>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormControl component="fieldset" fullWidth>
                            {/* {hidden input field for inserting value from state} */}
                            <input hidden type="checkbox" {...register('archPosition')} />
                            <ol start={2}>
                                <li>
                                    <RadioRhf
                                        question={getDiffRetainerLang(`formQuestionLabel.${questions[2]?.name}`)}
                                        errors={errors}
                                        name={questions[2].name}
                                        control={control}
                                        defaultValue={questions[2]?.defaultValue || 'false'}
                                        formControlLabelRhf={questions[2].option.map((v, _) => (
                                            <RadioOptionRhf value={v.value} label={getGlobalLang(`const.${v.label}`)} />
                                        ))}
                                    />
                                </li>

                                <li>
                                    <RadioRhf
                                        row={false}
                                        question={getDiffRetainerLang(`formQuestionLabel.${questions[3].name}`)}
                                        errors={errors}
                                        name={questions[3].name}
                                        control={control}
                                        formControlLabelRhf={
                                            <>
                                                <RetainerTypeFormControlLabelRhf
                                                    question={questions}
                                                    fixedWire={formValues[questions[2].name]}
                                                    selectedRetainer={formValues[questions[3].name]}
                                                    control={control}
                                                    errors={errors}
                                                    isTab={isTab}
                                                />
                                            </>
                                        }
                                    />
                                </li>

                                {enableOccBiteRampQuestion && (
                                    <li>
                                        <div style={{ marginTop: 24 }}>
                                            <div style={{ color: enableOccBiteRampQuestion ? 'black' : 'gray' }}>
                                                <TeethEquipmentRhf
                                                    option={questions[4].option}
                                                    question={getDiffRetainerLang(
                                                        `formQuestionLabel.${questions[4].name}`
                                                    )}
                                                    name={questions[4].name}
                                                    control={control}
                                                    errors={errors}
                                                    defaultValue={''}
                                                    row={false}
                                                    disable={!enableOccBiteRampQuestion}
                                                    teethSelectionDisabled={
                                                        formValues['occlusalBiteRampQuestion'] === 'false' ||
                                                        formValues['occlusalBiteRampQuestion'] === undefined
                                                    }
                                                    selectedTooth={
                                                        !isTab ? toothForBiteRamp : diffRetainer.toothBiteRamp
                                                    }
                                                    setSelectedTooth={setToothForBiteRamp}
                                                    showTeethSelection={
                                                        formValues['occlusalBiteRampQuestion'] === 'true'
                                                    }
                                                    isTab={isTab}
                                                    impressionType={impressionType}
                                                    radioOnChange={(e) => {
                                                        if (e.target.value === 'false') {
                                                            setToothForBiteRamp([]);
                                                            dispatch({ type: RESET_TOOTH_SELECTIONS });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                )}

                                {(enableClearRetainerDesignQuestion ) && (
                                    <li>
                                        <div style={{ marginTop: 24 }}>
                                            {
                                                <div
                                                    style={{
                                                        color: (enableClearRetainerDesignQuestion) ? 'black' : 'gray',
                                                    }}
                                                >
                                                    <ClearRetainerRhf
                                                        questionData={{ A: questions['5A'], B: questions['5B'] }}
                                                        // question={getDiffRetainerLang(`formQuestionLabel.${questions['5A'].name}`)}
                                                        question={retainerDesignQuestion}
                                                        control={control}
                                                        errors={errors}
                                                        row={false}
                                                        disable={!enableClearRetainerDesignQuestion}
                                                        formValues={formValues}
                                                        impressionType={impressionType}
                                                    />
                                                </div>
                                            }
                                        </div>
                                    </li>
                                )}

                                {/* {enableClearRetainerDesignQuestionForSecondSetRetainer && (
                                <li>
                                    <div style={{ marginTop: 24 }}>
                                        <div style={{ color: enableClearRetainerDesignQuestionForSecondSetRetainer ? 'black' : 'gray' }}>
                                                <ClearRetainerRhf
                                                questionData={{'A' : questions['6A'] , 'B' : questions['6B']}} 
                                                question={getDiffRetainerLang(`formQuestionLabel.${questions['6A'].name}`)} 
                                                control={control} 
                                                errors={errors} 
                                                row={false}
                                                disable={!enableClearRetainerDesignQuestionForSecondSetRetainer} 
                                                formValues={formValues}
                                                impressionType={impressionType}
                                                />
                                        </div>
                                    </div>
                                </li>

                            )} */}

                                {enablePontic && (
                                    <>
                                        <li>
                                            <RadioRhf
                                                question={getDiffRetainerLang(
                                                    `formQuestionLabel.${questions['pontic']?.name}_v2`
                                                )}
                                                errors={errors}
                                                name={questions['pontic'].name}
                                                control={control}
                                                row={false}
                                                // defaultValue={questions['pontic'].defaultValue}
                                                formControlLabelRhf={memoPonticOption.map((v, _) => (
                                                    <RadioOptionRhf value={v.value} label={v.label} />
                                                ))}
                                            />
                                        </li>

                                        {formValues['pontic'] === 'colourPonticRequired' && (
                                            <li>
                                                <SelectRhf
                                                    control={control}
                                                    // defaultValue={questions['ponticColor'].defaultValue}
                                                    option={questions['ponticColor'].option}
                                                    name={questions['ponticColor'].name}
                                                    label={getDiffRetainerLang(
                                                        `formQuestionLabel.${questions['ponticColor']?.name}_v2`
                                                    )}
                                                    errors={errors}
                                                />
                                            </li>
                                        )}
                                    </>
                                )}
                            </ol>
                        </FormControl>
                    </AccordionDetails>
                </Accordion>

                <FileUploadQuestion
                    impressionType={impressionType}
                    question={questions[8]}
                    errors={errors}
                    control={control}
                    enableUploadImpression={enableUploadImpression}
                    threeshapeConnected={threeshapeConnected}
                    setThreeshapeConnected={setThreeshapeConnected}
                    isNewCase={isNewCase}
                    isReSubmit={isReSubmit}
                />

                <SpecialInstruction
                    impressionType={impressionType}
                    value={specialInstruction}
                    onChange={(e) => {
                        setSpecialInstruction(e.target.value);
                        if (isTab) {
                            dispatch({ type: SET_SPECIAL_INSTRUCTION, payload: e.target.value });
                        }
                    }}
                />
            </div>

            <PrescriptionBtnGroup
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                expanded={expanded}
                setExpanded={setExpanded}
                impressionType={impressionType}
                setImpressionType={setImpressionType}
                reset={reset}
                formValues={formValues}
                onError={onError}
                isNewCase={isNewCase}
                resetAddOnFields={resetAddOnFields}
                errors={errors}
            />
        </>
    );
};
